import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  cancelSalaryAdjustment,
  createSalaryAdjustments,
  fetchSalaryAdjustmentFormMeta,
  listAllSalaryAdjustments,
  periodKey,
  periodLabel,
  salaryAdjustmentsApiErrorMessage,
  updateSalaryAdjustment,
  type EmployeeSalaryAdjustmentRow,
  type SalaryAdjustmentFormMeta,
  type SalaryAdjustmentType,
} from "@/lib/hr/salaryAdjustmentsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarRange,
  Check,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const FETCH_CAP = 500;

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type AdjustmentSortBy = "updated_desc" | "updated_asc" | "period_desc" | "period_asc";

type DraftLine = {
  clientId: string;
  type: SalaryAdjustmentType;
  title: string;
  amount: string;
  periodYear: number;
  periodMonth: number;
  reason: string;
};

type Draft = {
  employeeId: string;
  lines: DraftLine[];
};

function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}-${Math.random()}`;
}

function emptyLine(): DraftLine {
  return {
    clientId: newClientId(),
    type: "addition",
    title: "",
    amount: "",
    periodYear: 0,
    periodMonth: 0,
    reason: "",
  };
}

const emptyDraft = (employeeId = ""): Draft => ({
  employeeId,
  lines: [emptyLine()],
});

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeePickerLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function employeeLabel(employees: EmployeeRecord[], employeeId: string, fallbackName?: string | null): string {
  const e = employees.find((x) => x.id === employeeId);
  if (e) return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
  if (fallbackName?.trim()) return fallbackName.trim();
  return employeeId || "—";
}

function employeeBadge(
  employees: EmployeeRecord[],
  employeeId: string,
  fallbackName?: string | null,
): { label: string; className: string } {
  const label = employeeLabel(employees, employeeId, fallbackName);
  const palettes = [
    "bg-sky-500/15 text-sky-700",
    "bg-emerald-500/15 text-emerald-700",
    "bg-violet-500/15 text-violet-700",
    "bg-amber-500/15 text-amber-700",
    "bg-rose-500/15 text-rose-700",
    "bg-teal-500/15 text-teal-700",
  ] as const;
  let h = 0;
  for (let i = 0; i < employeeId.length; i += 1) h = (h * 31 + employeeId.charCodeAt(i)) >>> 0;
  return { label, className: palettes[h % palettes.length] };
}

function typeLabel(type: SalaryAdjustmentType): string {
  return type === "deduction" ? "Deduction" : "Addition";
}

function statusLabel(status: EmployeeSalaryAdjustmentRow["status"]): string {
  if (status === "applied") return "Applied";
  if (status === "cancelled") return "Cancelled";
  return "Pending";
}

function statusBadgeVariant(status: EmployeeSalaryAdjustmentRow["status"]): "default" | "secondary" | "outline" | "destructive" {
  if (status === "applied") return "secondary";
  if (status === "cancelled") return "outline";
  return "default";
}

function buildPeriodOptions(
  meta: SalaryAdjustmentFormMeta | null,
): Array<{ year: number; month: number; key: string; label: string }> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];

  // Current year Jan–Dec, then continue through the next calendar year (24 months from Jan).
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    if (blocked.has(key)) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }

  return options;
}

function SalaryPeriodPicker({
  open,
  onOpenChange,
  periodYear,
  periodMonth,
  periodOptions,
  pickerYear,
  onPickerYearChange,
  onSelect,
  disabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodYear: number;
  periodMonth: number;
  periodOptions: Array<{ year: number; month: number; key: string; label: string }>;
  pickerYear: number;
  onPickerYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
  disabled?: boolean;
}) {
  const availableKeys = new Set(periodOptions.map((opt) => opt.key));
  const minPickerYear = periodOptions[0]?.year ?? new Date().getFullYear();
  const maxPickerYear = periodOptions[periodOptions.length - 1]?.year ?? minPickerYear;
  const selectedLabel =
    periodYear && periodMonth ? periodLabel(periodYear, periodMonth, MONTHS) : null;

  return (
    <Popover modal={true} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "glass-input h-10 w-full justify-start text-left font-normal",
            !selectedLabel && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          <CalendarRange className="mr-2 h-4 w-4 shrink-0 opacity-70" />
          {selectedLabel ?? "Select period"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[min(100vw-2rem,20rem)] p-4" align="start" sideOffset={6}>
        <div className="mb-3 space-y-1">
          <p className="text-sm font-semibold text-foreground">Select salary period</p>
          <p className="text-xs text-muted-foreground">Select month and year for this adjustment.</p>
        </div>

        {periodOptions.length === 0 ? (
          <p className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center text-xs text-muted-foreground">
            No available salary periods for this employee.
          </p>
        ) : (
          <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear <= minPickerYear}
                onClick={() => onPickerYearChange(Math.max(minPickerYear, pickerYear - 1))}
                aria-label="Previous year"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold tabular-nums text-foreground">{pickerYear}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear >= maxPickerYear}
                onClick={() => onPickerYearChange(Math.min(maxPickerYear, pickerYear + 1))}
                aria-label="Next year"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTH_LABELS.map((label, monthIndex) => {
                const month = monthIndex + 1;
                const key = periodKey(pickerYear, month);
                const isAvailable = availableKeys.has(key);
                const isSelected = periodYear === pickerYear && periodMonth === month;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => onSelect(pickerYear, month)}
                    className={cn(
                      "h-10 rounded-md text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : isAvailable
                          ? "border border-border/50 bg-background text-foreground hover:bg-muted"
                          : "cursor-not-allowed border border-transparent bg-muted/30 text-muted-foreground/50",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{selectedLabel ?? "None"}</span>
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function SalaryAdjustments() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_adjustment_can_create");
  const canCancel = can(user, "salary_adjustment_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);

  const [rows, setRows] = useState<EmployeeSalaryAdjustmentRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [sortBy, setSortBy] = useState<AdjustmentSortBy>("updated_desc");
  const [quickDate, setQuickDate] = useState<"today" | "week" | "month" | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const isEditing = Boolean(editingId);

  const [formMeta, setFormMeta] = useState<SalaryAdjustmentFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<EmployeeSalaryAdjustmentRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [periodPickerLineId, setPeriodPickerLineId] = useState<string | null>(null);
  const [periodPickerYear, setPeriodPickerYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllSalaryAdjustments({ page: 1, perPage: FETCH_CAP });
      setRows(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(salaryAdjustmentsApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!modalOpen || !draft.employeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchSalaryAdjustmentFormMeta(draft.employeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(salaryAdjustmentsApiErrorMessage(e));
          setFormMeta(null);
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, draft.employeeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterEmployee !== "all" && row.employeeId !== filterEmployee) return false;
      if (quickDate !== "all") {
        const updated = parseISO(row.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (!q) return true;
      const empName = employeeLabel(employees, row.employeeId, row.employeeName).toLowerCase();
      const hay = `${empName} ${row.title} ${row.type} ${row.amount} ${periodLabel(row.periodYear, row.periodMonth, MONTHS)} ${row.status} ${row.reason ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, filterEmployee, quickDate, employees]);

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = periodKey(a.periodYear, a.periodMonth);
      const bPeriod = periodKey(b.periodYear, b.periodMonth);
      switch (sortBy) {
        case "updated_asc":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "period_desc":
          return bPeriod.localeCompare(aPeriod) || b.id.localeCompare(a.id);
        case "period_asc":
          return aPeriod.localeCompare(bPeriod) || a.id.localeCompare(b.id);
        case "updated_desc":
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = filterEmployee !== "all" ? 1 : 0;
  const resetFilters = () => setFilterEmployee("all");

  const paginationKey = `${query}|${filterEmployee}|${quickDate}|${sortBy}`;
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    paginatedItems,
    totalCount,
    totalPages,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sortedFiltered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const periodOptions = useMemo(() => {
    const options = buildPeriodOptions(formMeta);
    const line = draft.lines[0];
    if (!isEditing || !line?.periodYear || !line?.periodMonth) return options;
    const key = periodKey(line.periodYear, line.periodMonth);
    if (options.some((opt) => opt.key === key)) return options;
    return [
      { year: line.periodYear, month: line.periodMonth, key, label: periodLabel(line.periodYear, line.periodMonth, MONTHS) },
      ...options,
    ];
  }, [formMeta, isEditing, draft.lines]);

  const openCreate = () => {
    if (!employees.length) {
      toast.error("No employees available.");
      return;
    }
    setEditingId(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const canMutatePending = (row: EmployeeSalaryAdjustmentRow) =>
    row.status === "pending" && (row.salaryId == null || row.salaryId === "");

  const openEdit = (row: EmployeeSalaryAdjustmentRow) => {
    if (!canMutatePending(row)) {
      toast.error(
        row.salaryId
          ? "This adjustment is linked to a salary process and can no longer be edited."
          : "Only pending adjustments can be edited.",
      );
      return;
    }
    setEditingId(row.id);
    setDraft({
      employeeId: row.employeeId,
      lines: [
        {
          clientId: newClientId(),
          type: row.type,
          title: row.title || "",
          amount: formatMoney(String(row.amount)),
          periodYear: row.periodYear,
          periodMonth: row.periodMonth,
          reason: row.reason || "",
        },
      ],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setDraft(emptyDraft());
    setFormMeta(null);
    setPeriodPickerLineId(null);
    setEmployeePickerOpen(false);
  };

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)),
    }));
  };

  const addLine = () => {
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyLine()] }));
  };

  const removeLine = (clientId: string) => {
    setDraft((d) => {
      if (d.lines.length <= 1) return d;
      return { ...d, lines: d.lines.filter((line) => line.clientId !== clientId) };
    });
  };

  const save = async () => {
    if (saving) return;
    if (!draft.employeeId) return toast.error("Select an employee.");
    if (!formMeta) return toast.error("Available periods are still loading.");

    if (isEditing && editingId) {
      const line = draft.lines[0];
      if (!line) return toast.error("Adjustment details are missing.");
      if (!line.title.trim()) return toast.error("Title is required.");
      const amount = safeNumber(line.amount);
      if (!amount || amount <= 0) return toast.error("Amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period.");

      setSaving(true);
      try {
        const result = await updateSalaryAdjustment({
          id: editingId,
          employeeId: draft.employeeId,
          type: line.type,
          title: line.title.trim(),
          amount,
          periodYear: line.periodYear,
          periodMonth: line.periodMonth,
          reason: line.reason.trim() || null,
        });
        toast.success(result.message);
        closeModal();
        await load();
      } catch (e) {
        toast.error(salaryAdjustmentsApiErrorMessage(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    const payloadLines: Array<{
      type: SalaryAdjustmentType;
      title: string;
      amount: number;
      periodYear: number;
      periodMonth: number;
      reason: string | null;
    }> = [];

    for (const line of draft.lines) {
      if (!line.title.trim()) return toast.error("Each adjustment needs a title.");
      const amount = safeNumber(line.amount);
      if (!amount || amount <= 0) return toast.error("Each amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period for each adjustment line.");
      payloadLines.push({
        type: line.type,
        title: line.title.trim(),
        amount,
        periodYear: line.periodYear,
        periodMonth: line.periodMonth,
        reason: line.reason.trim() || null,
      });
    }

    setSaving(true);
    try {
      const result = await createSalaryAdjustments({ employeeId: draft.employeeId, lines: payloadLines });
      toast.success(result.message);
      closeModal();
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdjustmentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    try {
      const result = await cancelSalaryAdjustment(cancelTarget.id);
      toast.success(result.message);
      setCancelTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdjustmentsApiErrorMessage(e));
    } finally {
      setCancelling(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Salary adjustment access required"
      message="You don’t have permission to view salary adjustments. Ask an administrator to grant salary_adjustment_can_view_any."
    />
  ) : (
    <Layout title="Salary adjustments">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Salary adjustments</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record additions and deductions by salary period — each line is applied when that period&apos;s payroll is processed.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate} disabled={!employees.length}>
              <Plus className="w-4 h-4" />
              Add salary adjustment
            </Button>
          ) : null}
        </div>

        <Dialog open={modalOpen} onOpenChange={(o) => (o ? setModalOpen(true) : closeModal())}>
          <DialogContent className="max-w-5xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit salary adjustment" : "New salary adjustment"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <Popover
                  modal
                  open={employeePickerOpen}
                  onOpenChange={(next) => {
                    if (isEditing) return;
                    setEmployeePickerOpen(next);
                    if (next) setEmployeePickerCycle((c) => c + 1);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeePickerOpen}
                      disabled={isEditing}
                      className={cn(
                        "glass-input h-9 w-full justify-between px-3 font-normal",
                        !draft.employeeId && "text-muted-foreground",
                      )}
                    >
                      {draft.employeeId ? (
                        <span className="flex items-center gap-2 truncate">
                          {(() => {
                            const sel = employees.find((e) => e.id === draft.employeeId);
                            if (!sel) return "Select employee…";
                            return (
                              <>
                                <Avatar className="h-5 w-5 text-[8px]">
                                  {sel.photoDataUrl ? (
                                    <AvatarImage src={sel.photoDataUrl} alt={employeeDisplayName(sel)} />
                                  ) : null}
                                  <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                    {employeeInitials(sel)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate">{employeePickerLabel(sel)}</span>
                              </>
                            );
                          })()}
                        </span>
                      ) : (
                        <span className="truncate text-left">Select employee…</span>
                      )}
                      {!isEditing ? <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" /> : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    sideOffset={4}
                  >
                    <Command key={employeePickerCycle} shouldFilter>
                      <CommandInput placeholder="Search name, code or department…" className="h-9" />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          {employees.map((e) => {
                            const label = employeePickerLabel(e);
                            const subtitle = employeeSubtitle(e);
                            const selected = draft.employeeId === e.id;
                            const searchValue = [
                              label,
                              e.employeeCode,
                              e.departmentName,
                              e.designationName,
                              e.id,
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <CommandItem
                                key={e.id}
                                value={searchValue}
                                onSelect={() => {
                                  setDraft((d) => ({ ...d, employeeId: e.id }));
                                  setEmployeePickerOpen(false);
                                }}
                                className="gap-2"
                              >
                                <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                  {e.photoDataUrl ? (
                                    <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                  ) : null}
                                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                    {employeeInitials(e)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate text-sm font-medium">{label}</span>
                                  {subtitle ? (
                                    <span className="truncate text-[11px] opacity-60 leading-tight">
                                      {subtitle}
                                    </span>
                                  ) : null}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {draft.employeeId && metaLoading ? (
                <div className="rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading available periods…
                  </span>
                </div>
              ) : null}

              <div className="space-y-3">
                  <div className={cn(
                    "hidden md:grid gap-3 px-1",
                    isEditing
                      ? "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)]"
                      : "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)_auto]",
                  )}>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Label className="text-xs text-muted-foreground">Title</Label>
                    <Label className="text-xs text-muted-foreground">Amount</Label>
                    <Label className="text-xs text-muted-foreground">Salary period</Label>
                    <Label className="text-xs text-muted-foreground">Reason</Label>
                    {!isEditing ? <span className="sr-only">Actions</span> : null}
                  </div>

                  {draft.lines.map((line, index) => (
                    <div
                      key={line.clientId}
                      className={cn(
                        "grid grid-cols-1 gap-3 items-end rounded-lg border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none",
                        isEditing
                          ? "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)]"
                          : "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)_auto]",
                      )}
                    >
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Type</Label> : null}
                        <Select
                          value={line.type}
                          onValueChange={(v) => updateLine(line.clientId, { type: v as SalaryAdjustmentType })}
                        >
                          <SelectTrigger className="glass-input h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="addition">Addition</SelectItem>
                            <SelectItem value="deduction">Deduction</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Title</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.title}
                          onChange={(e) => updateLine(line.clientId, { title: e.target.value })}
                          placeholder="e.g. Bonus"
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Amount</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.amount}
                          onChange={(e) => updateLine(line.clientId, { amount: formatMoney(e.target.value) })}
                          placeholder="e.g. 10,000"
                          inputMode="decimal"
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Salary period</Label> : null}
                        <SalaryPeriodPicker
                          open={periodPickerLineId === line.clientId}
                          onOpenChange={(open) => {
                            if (open) {
                              setPeriodPickerLineId(line.clientId);
                              setPeriodPickerYear(
                                line.periodYear || periodOptions[0]?.year || new Date().getFullYear(),
                              );
                            } else {
                              setPeriodPickerLineId((id) => (id === line.clientId ? null : id));
                            }
                          }}
                          periodYear={line.periodYear}
                          periodMonth={line.periodMonth}
                          periodOptions={periodOptions}
                          pickerYear={periodPickerYear}
                          onPickerYearChange={setPeriodPickerYear}
                          onSelect={(year, month) => {
                            updateLine(line.clientId, { periodYear: year, periodMonth: month });
                            setPeriodPickerLineId(null);
                          }}
                          disabled={Boolean(draft.employeeId) && metaLoading}
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Reason</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.reason}
                          onChange={(e) => updateLine(line.clientId, { reason: e.target.value })}
                          placeholder="Optional reason"
                        />
                      </div>
                      {!isEditing ? (
                        <div className="flex items-center gap-2 pb-0.5">
                          {index === draft.lines.length - 1 ? (
                            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={addLine} aria-label="Add another line">
                              <Plus className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 shrink-0 text-destructive"
                              onClick={() => removeLine(line.clientId)}
                              aria-label="Remove line"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" className="gap-2" onClick={() => void save()} disabled={saving || !draft.employeeId || metaLoading}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isEditing ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!cancelTarget}
          onOpenChange={(o) => !o && setCancelTarget(null)}
          title="Cancel salary adjustment?"
          description={
            cancelTarget
              ? `This will cancel “${cancelTarget.title}” (${typeLabel(cancelTarget.type).toLowerCase()} · ${formatAmountCommas(cancelTarget.amount)}) for ${employeeLabel(employees, cancelTarget.employeeId, cancelTarget.employeeName)}.`
              : ""
          }
          cancelLabel="Keep"
          confirmLabel={cancelling ? "Cancelling..." : "Cancel adjustment"}
          onConfirm={() => void confirmCancel()}
        />

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employee, title, amount, period…"
                />
              </div>
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter salary adjustments</SheetTitle>
                    <SheetDescription>Refine by employee.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All employees</SelectItem>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {employeeLabel(employees, e.id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <SheetFooter className="mt-6 gap-2">
                    <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button type="button" variant="premium" className="w-full">
                        Apply filters
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect id="adjustment-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AdjustmentSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="period_desc">Period: Newest first</SelectItem>
                    <SelectItem value="period_asc">Period: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button type="button" onClick={() => setViewMode("grid")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="Grid view">
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode("list")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="List view">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && sortedFiltered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No salary adjustments found for these filters.</div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedItems.map((row, i) => {
                const TypeIcon = row.type === "deduction" ? ArrowDownLeft : ArrowUpRight;
                return (
                  <motion.div key={row.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <CatalogEntityCard
                      title={row.title || "Adjustment"}
                      statusBadge={employeeBadge(employees, row.employeeId, row.employeeName)}
                      primaryMeta={{
                        label: "Period",
                        value: periodLabel(row.periodYear, row.periodMonth, MONTHS),
                        icon: CalendarRange,
                        iconClassName: "text-sky-600",
                      }}
                      secondaryMeta={{
                        label: "Type",
                        value: `${typeLabel(row.type)} · ${formatAmountCommas(row.amount)}`,
                        icon: TypeIcon,
                        iconClassName: row.type === "deduction" ? "text-rose-600" : "text-emerald-600",
                      }}
                      footer={`Status: ${statusLabel(row.status)} · Updated ${formatCatalogAuditDate(row.updatedAt)}`}
                      onEdit={canCreate && canMutatePending(row) ? () => openEdit(row) : undefined}
                      onDelete={canCancel && canMutatePending(row) ? () => setCancelTarget(row) : undefined}
                    />
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "list" ? (
            <div className="space-y-3">
              {paginatedItems.map((row, i) => {
                const TypeIcon = row.type === "deduction" ? ArrowDownLeft : ArrowUpRight;
                const emp = employeeBadge(employees, row.employeeId, row.employeeName);
                return (
                  <motion.div key={row.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="h-14 w-16 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                        <TypeIcon className={cn("w-7 h-7", row.type === "deduction" ? "text-rose-500/70" : "text-emerald-500/70")} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-foreground truncate">
                          <span className={cn("mr-2 rounded-full px-2 py-0.5 text-[11px] font-semibold", emp.className)}>
                            {emp.label}
                          </span>
                          {row.title || "Adjustment"}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {typeLabel(row.type)} · {formatAmountCommas(row.amount)} · {periodLabel(row.periodYear, row.periodMonth, MONTHS)}
                        </p>
                        {row.reason ? <p className="text-xs text-muted-foreground mt-1">{row.reason}</p> : null}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={statusBadgeVariant(row.status)}>{statusLabel(row.status)}</Badge>
                        {canCreate && canMutatePending(row) ? (
                          <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => openEdit(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        ) : null}
                        {canCancel && canMutatePending(row) ? (
                          <Button variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => setCancelTarget(row)}>
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 ? (
            <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
